import { ElementType, ReactNode } from 'react';
import styled from 'styled-components';

export type TypographyProps = {
  children: string | ReactNode | ReactNode[];
  type?: string;
  tag?: string;
  color?: string;
  colorVariant?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  textDecoration?: string;
  display?: string;
  as?: ElementType;
  noWrap?: boolean;
};

interface IDictionary {
  [index: string]: string;
}

const defaultProps = {
  type: 'headline1',
  tag: null,
  color: null,
  as: null,
  colorVariant: 'gray100',
  marginTop: '0rem',
  marginBottom: '0rem',
  marginLeft: '0rem',
  marginRight: '0rem',
  textDecoration: 'none',
  display: 'block',
  noWrap: false,
};

const getStyleColor = (
  colors: IDictionary,
  colorVariant?: string,
  color?: string
): string => {
  if (color) return color;
  if (colorVariant) return colors[colorVariant];
  return 'var(--gray-100)';
};

const getDisplay = (type: string, display?: string): string => {
  const defaultDisplay: IDictionary = {
    headline1: 'block',
    headline2: 'block',
    headline3: 'block',
    headline4: 'block',
    body1: 'block',
    body2: 'inline',
    overline: 'inline',
    caption: 'inline',
    buttons: 'inline',
    subtitle1: 'inline',
    subtitle2: 'inline',
    emphasis: 'inline',
    strong: 'inline',
    delete: 'inline',
  };

  if (display) return display;
  return defaultDisplay[type];
};

const getDefaultTagByType = (type?: string, tag?: string): string => {
  const defaultTag: IDictionary = {
    headline1: 'h1',
    headline2: 'h2',
    headline3: 'h3',
    headline4: 'h4',
    body1: 'p',
    body2: 'p',
    overline: 'p',
    caption: 'span',
    buttons: 'p',
    subtitle1: 'p',
    subtitle2: 'p',
    emphasis: 'em',
    strong: 'strong',
    delete: 'del',
  };

  if (tag) return tag;
  if (type) return defaultTag[type];
  return 'headline1';
};

const Headline1 = styled.h1<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 2rem;
  font-weight: 700;
  line-height: 2.5rem;
  display: ${(props): string => getDisplay('headline1', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 2.5rem;
    line-height: 3rem;
  }
`;
const Headline2 = styled.h2<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.75rem;
  display: ${(props): string => getDisplay('headline2', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 2.215rem;
    line-height: 2.5rem;
  }
`;
const Headline3 = styled.h3<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.75rem;
  display: ${(props): string => getDisplay('headline3', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 1.5rem;
    line-height: 2rem;
  }
`;
const Headline4 = styled.h4<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5rem;
  display: ${(props): string => getDisplay('headline4', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.75rem;
  }
`;
const Body1 = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.625rem;
  display: ${(props): string => getDisplay('body1', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;
const Body2 = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5rem;
  display: ${(props): string => getDisplay('body2', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    line-height: 1.25rem;
  }
`;
const Overline = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.063rem;
  line-height: 1.25rem;
  display: ${(props): string => getDisplay('overline', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 0.875rem;
  }
`;
const Caption = styled.span<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1rem;
  display: ${(props): string => getDisplay('caption', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;
const Buttons = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.25rem;
  display: ${(props): string => getDisplay('buttons', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    line-height: 1.375rem;
  }
`;
const Subtitle1 = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.25rem;
  display: ${(props): string => getDisplay('subtitle1', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;
const Subtitle2 = styled.p<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  display: ${(props): string => getDisplay('subtitle2', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
  @media ${({ theme }): string => theme.devices.laptop} {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`;
const Emphasis = styled.em<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-style: italic;
  line-height: 1.625rem;
  display: ${(props): string => getDisplay('emphasis', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;
const Strong = styled.strong<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.625rem;
  display: ${(props): string => getDisplay('strong', props.display)};
  text-decoration: ${(props): string | undefined => props.textDecoration};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;
const Delete = styled.del<TypographyProps>`
  font-family: var(--primary-font);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.625rem;
  display: ${(props): string => getDisplay('delete', props.display)};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
  white-space: ${(props): string => (props.noWrap ? 'nowrap' : 'normal')};
  color: ${(props): string =>
    getStyleColor(props.theme.colors, props.colorVariant, props.color)};
`;

const Typography = (props: TypographyProps): JSX.Element => {
  const { type, tag } = props;
  const renderTag = getDefaultTagByType(type, tag);

  switch (props.type) {
    case 'headline1':
      return (
        <Headline1 as={renderTag} {...props}>
          {props.children}
        </Headline1>
      );
    case 'headline2':
      return (
        <Headline2 as={renderTag} {...props}>
          {props.children}
        </Headline2>
      );
    case 'headline3':
      return (
        <Headline3 as={renderTag} {...props}>
          {props.children}
        </Headline3>
      );
    case 'headline4':
      return (
        <Headline4 as={renderTag} {...props}>
          {props.children}
        </Headline4>
      );
    case 'body1':
      return (
        <Body1 as={renderTag} {...props}>
          {props.children}
        </Body1>
      );
    case 'body2':
      return (
        <Body2 as={renderTag} {...props}>
          {props.children}
        </Body2>
      );
    case 'buttons':
      return (
        <Buttons as={renderTag} {...props}>
          {props.children}
        </Buttons>
      );
    case 'subtitle1':
      return (
        <Subtitle1 as={renderTag} {...props}>
          {props.children}
        </Subtitle1>
      );
    case 'subtitle2':
      return (
        <Subtitle2 as={renderTag} {...props}>
          {props.children}
        </Subtitle2>
      );
    case 'overline':
      return (
        <Overline as={renderTag} {...props}>
          {props.children}
        </Overline>
      );
    case 'caption':
      return (
        <Caption as={renderTag} {...props}>
          {props.children}
        </Caption>
      );
    case 'emphasis':
      return (
        <Emphasis as={renderTag} {...props}>
          {props.children}
        </Emphasis>
      );
    case 'strong':
      return (
        <Strong as={renderTag} {...props}>
          {props.children}
        </Strong>
      );
    case 'delete':
      return (
        <Delete as={renderTag} {...props}>
          {props.children}
        </Delete>
      );
    default:
      return (
        <Headline1 as={renderTag} {...props}>
          {props.children}
        </Headline1>
      );
  }
};

Typography.defaultProps = defaultProps;

export default Typography;
